import { useCallback, useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  emailMonitorDecisionService,
} from '../services/emailMonitorDecisions';
import type {
  EmailMonitorDecision,
  DecisionStatsResponse,
  PipelineStatus,
  RoutingRulesResponse,
  ErrorEmail,
  SortField,
  SortOrder,
} from '../services/emailMonitorDecisions';
import { ClassificationsPanel } from '../components/email-monitor';
import RecommendationText from '../components/portal/RecommendationText';
import styles from './EmailMonitorDecisionsPage.module.css';

type ActiveTab = 'decisions' | 'classifications';

const DECISION_COLORS: Record<string, string> = {
  notify: '#27ae60',
  unsure: '#f39c12',
  ignore: '#95a5a6',
};

const DECISION_LABELS: Record<string, string> = {
  notify: 'Notify',
  unsure: 'Unsure',
  ignore: 'Ignore',
};

const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  email_follow_up_needed: 'Follow-up Needed',
  email_urgent_request: 'Urgent Request',
  email_complaint: 'Complaint',
  email_new_lead: 'New Lead',
  email_delivery_issue: 'Delivery Issue',
  email_pricing_request: 'Pricing Request',
  email_general_fyi: 'General FYI',
};

// Sortable column definitions
interface ColumnDef {
  key: SortField | '';
  label: string;
  sortable: boolean;
}

const COLUMNS: ColumnDef[] = [
  { key: 'created_at', label: 'Date', sortable: true },
  { key: 'decision', label: 'Decision', sortable: true },
  { key: 'notification_type', label: 'Type', sortable: true },
  { key: '', label: 'Subject / From', sortable: false },
  { key: 'confidence', label: 'Confidence', sortable: true },
  { key: 'total_tokens', label: 'Tokens', sortable: true },
  { key: 'total_cost_usd', label: 'Cost', sortable: true },
  { key: 'latency_ms', label: 'Latency', sortable: true },
  { key: 'salesperson_no', label: 'Salesperson', sortable: true },
];

export default function EmailMonitorDecisionsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkHandled = useRef(false);

  const [decisions, setDecisions] = useState<EmailMonitorDecision[]>([]);
  const [stats, setStats] = useState<DecisionStatsResponse | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  // Filters
  const [decisionFilter, setDecisionFilter] = useState<string>('');
  const [notificationTypeFilter, setNotificationTypeFilter] = useState<string>('');
  const [notificationTypes, setNotificationTypes] = useState<string[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  // Sorting
  const [sortBy, setSortBy] = useState<SortField>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  // Pagination
  const [page, setPage] = useState(0);
  const limit = 25;

  // Detail modal
  const [selectedDecision, setSelectedDecision] = useState<EmailMonitorDecision | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Deep-link error: surface "no decision found for emailId=X" instead of failing silently.
  // Without this, clicking "View Agent Run" on an email that hasn't completed classification
  // (e.g. stuck in PROCESSING) just opens the page with no feedback.
  const [deepLinkNotFound, setDeepLinkNotFound] = useState<string | null>(null);

  // Pipeline control
  const [pipelineStatus, setPipelineStatus] = useState<PipelineStatus | null>(null);
  const [batchRunning, setBatchRunning] = useState(false);
  const [batchMessage, setBatchMessage] = useState('');
  const [schedulerToggling, setSchedulerToggling] = useState(false);
  const [batchSize, setBatchSize] = useState(100);

  // Routing rules
  const [routingRules, setRoutingRules] = useState<RoutingRulesResponse | null>(null);
  const [routingRulesOpen, setRoutingRulesOpen] = useState(false);

  // Page-level tab
  const [activeTab, setActiveTab] = useState<ActiveTab>('decisions');

  // Error emails (all errors, not just DLQ)
  const [errorEmails, setErrorEmails] = useState<ErrorEmail[]>([]);
  const [errorTotal, setErrorTotal] = useState(0);
  const [errorMaxRetries, setErrorMaxRetries] = useState(3);
  const [errorsOpen, setErrorsOpen] = useState(false);
  const [errorsLoading, setErrorsLoading] = useState(false);
  const [errorRetrying, setErrorRetrying] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  const loadPipelineStatus = useCallback(async () => {
    try {
      const status = await emailMonitorDecisionService.getPipelineStatus();
      setPipelineStatus(status);
    } catch {
      // Silently fail — user may not have admin access
    }
  }, []);

  const handleRunBatch = async () => {
    setBatchRunning(true);
    setBatchMessage('');
    try {
      const result = await emailMonitorDecisionService.runBatch({ batch_size: batchSize });
      setBatchMessage(result.message);
      await loadPipelineStatus();
      // Refresh table data after a short delay
      setTimeout(() => loadData(), 2000);
    } catch (err) {
      setBatchMessage('Failed to run batch. Check console for details.');
      console.error('Run batch failed:', err);
    } finally {
      setBatchRunning(false);
    }
  };

  const [resetting, setResetting] = useState(false);

  const handleResetPipeline = async () => {
    if (!window.confirm(
      'This will delete ALL pipeline data: decisions, notifications, conversations, classifications, pipeline-created orders, and red flags. All emails will be reset to unprocessed. Continue?'
    )) return;
    setResetting(true);
    setBatchMessage('');
    try {
      const result = await emailMonitorDecisionService.resetPipeline();
      setBatchMessage(result.message);
      await loadPipelineStatus();
      loadData();
    } catch (err) {
      setBatchMessage('Failed to reset pipeline. Check console for details.');
      console.error('Reset pipeline failed:', err);
    } finally {
      setResetting(false);
    }
  };

  const handleToggleScheduler = async () => {
    if (!pipelineStatus) return;
    setSchedulerToggling(true);
    try {
      const result = await emailMonitorDecisionService.toggleScheduler(
        !pipelineStatus.scheduler_enabled,
      );
      setBatchMessage(result.message);
      await loadPipelineStatus();
    } catch (err) {
      setBatchMessage('Failed to toggle scheduler.');
      console.error('Toggle scheduler failed:', err);
    } finally {
      setSchedulerToggling(false);
    }
  };

  const loadErrorEmails = useCallback(async () => {
    setErrorsLoading(true);
    try {
      const res = await emailMonitorDecisionService.getErrorEmails(50, 0);
      setErrorEmails(res.emails);
      setErrorTotal(res.total);
      setErrorMaxRetries(res.max_retries);
    } catch {
      // Silently fail
    } finally {
      setErrorsLoading(false);
    }
  }, []);

  const handleRetryErrorEmail = async (emailId: string) => {
    setErrorRetrying(emailId);
    setErrorMessage('');
    try {
      const res = await emailMonitorDecisionService.retryErrorEmail(emailId);
      setErrorMessage(res.message);
      await Promise.all([loadErrorEmails(), loadPipelineStatus()]);
    } catch (err) {
      setErrorMessage('Failed to retry email. Check console.');
      console.error('Retry error email failed:', err);
    } finally {
      setErrorRetrying(null);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Load notification types, pipeline status, routing rules, and errors on mount
  useEffect(() => {
    emailMonitorDecisionService.getNotificationTypes().then(setNotificationTypes).catch(() => {});
    emailMonitorDecisionService.getRoutingRules().then(setRoutingRules).catch(() => {});
    loadPipelineStatus();
    loadErrorEmails();
  }, [loadPipelineStatus, loadErrorEmails]);

  // Load data when filters/sort/page change
  useEffect(() => {
    loadData();
  }, [decisionFilter, notificationTypeFilter, search, sortBy, sortOrder, page]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        emailMonitorDecisionService.listDecisions({
          decision: decisionFilter || undefined,
          notification_type: notificationTypeFilter || undefined,
          search: search || undefined,
          sort_by: sortBy,
          sort_order: sortOrder,
          limit,
          offset: page * limit,
        }),
        emailMonitorDecisionService.getStats(),
      ]);
      setDecisions(listRes.decisions);
      setTotal(listRes.total);
      setStats(statsRes);
    } catch (err) {
      console.error('Failed to load decisions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(0);
  };

  const openDetail = async (d: EmailMonitorDecision) => {
    setDetailLoading(true);
    try {
      const full = await emailMonitorDecisionService.getDecision(d.id);
      setSelectedDecision(full);
    } catch {
      setSelectedDecision(d);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedDecision(null);
    // Clear emailId deep-link param so re-navigation works
    if (searchParams.has('emailId')) {
      searchParams.delete('emailId');
      setSearchParams(searchParams, { replace: true });
    }
  };

  const dismissDeepLinkNotFound = () => {
    setDeepLinkNotFound(null);
    if (searchParams.has('emailId')) {
      searchParams.delete('emailId');
      setSearchParams(searchParams, { replace: true });
    }
  };

  // Deep-link: auto-open detail modal when ?emailId=<uuid> is present
  useEffect(() => {
    const deepLinkEmailId = searchParams.get('emailId');
    if (!deepLinkEmailId || deepLinkHandled.current) return;
    deepLinkHandled.current = true;

    (async () => {
      setDetailLoading(true);
      setDeepLinkNotFound(null);
      try {
        // Find the decision associated with this email
        const res = await emailMonitorDecisionService.listDecisions({
          email_id: deepLinkEmailId,
          limit: 1,
        });
        if (res.decisions.length > 0) {
          const full = await emailMonitorDecisionService.getDecision(res.decisions[0].id);
          setSelectedDecision(full);
        } else {
          // No decision exists for this email — most likely the unified pipeline
          // hasn't run (or got stuck) for this email. Surface the failure.
          setDeepLinkNotFound(deepLinkEmailId);
        }
      } catch (err) {
        console.error('Failed to deep-link to decision by emailId:', err);
        setDeepLinkNotFound(deepLinkEmailId);
      } finally {
        setDetailLoading(false);
      }
    })();
  }, [searchParams]);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleString();
  };

  const formatCost = (cost: number) => `$${cost.toFixed(4)}`;

  const getSortIcon = (field: SortField) => {
    if (sortBy !== field) return ' \u2195';
    return sortOrder === 'asc' ? ' \u2191' : ' \u2193';
  };

  const getEmailSubject = (ctx: Record<string, unknown>) =>
    String(ctx.email_subject || ctx.subject || '-');

  const getEmailFrom = (ctx: Record<string, unknown>) =>
    String(ctx.email_from || ctx.from || '-');

  const totalPages = Math.ceil(total / limit);

  return (
    <div className={styles.container}>
      <h1 className={styles.pageTitle}>Email Monitor</h1>
      <p className={styles.subtitle}>
        Unified email pipeline — agent decisions, classifications, and pipeline control
      </p>

      {/* Pipeline Control Panel */}
      {pipelineStatus && (
        <div className={styles.pipelinePanel}>
          <div className={styles.pipelineHeader}>
            <h3 className={styles.pipelinePanelTitle}>Pipeline Control</h3>
            <div className={styles.pipelineIndicator}>
              <span
                className={styles.statusDot}
                style={{
                  backgroundColor: pipelineStatus.scheduler_enabled ? '#27ae60' : '#95a5a6',
                }}
              />
              {pipelineStatus.scheduler_enabled ? 'Scheduler Active' : 'Scheduler Off'}
            </div>
          </div>

          <div className={styles.pipelineStats}>
            <div className={styles.pipelineStat}>
              <span className={styles.pipelineStatValue}>{pipelineStatus.never_processed_count}</span>
              <span className={styles.pipelineStatLabel}>Pending</span>
            </div>
            <div className={styles.pipelineStat}>
              <span className={styles.pipelineStatValue}>{pipelineStatus.processing_count}</span>
              <span className={styles.pipelineStatLabel}>Processing</span>
            </div>
            <div className={styles.pipelineStat}>
              <span className={styles.pipelineStatValue}>{pipelineStatus.queue_size}/{pipelineStatus.queue_capacity}</span>
              <span className={styles.pipelineStatLabel}>Queue</span>
            </div>
            <div
              className={styles.pipelineStat}
              onClick={pipelineStatus.error_count > 0 ? () => setErrorsOpen(!errorsOpen) : undefined}
              style={{ cursor: pipelineStatus.error_count > 0 ? 'pointer' : undefined }}
              title={pipelineStatus.error_count > 0 ? 'Click to view error details' : undefined}
            >
              <span className={styles.pipelineStatValue} style={{ color: pipelineStatus.error_count > 0 ? '#EE3C33' : undefined, textDecoration: pipelineStatus.error_count > 0 ? 'underline' : undefined }}>
                {pipelineStatus.error_count}
              </span>
              <span className={styles.pipelineStatLabel}>Errors</span>
            </div>
            <div className={styles.pipelineStat}>
              <span className={styles.pipelineStatValue} style={{ color: pipelineStatus.dead_letter_count > 0 ? '#c0392b' : undefined }}>
                {pipelineStatus.dead_letter_count}
              </span>
              <span className={styles.pipelineStatLabel}>Dead Letter</span>
            </div>
          </div>

          <div className={styles.pipelineActions}>
            <div className={styles.batchSizeGroup}>
              <input
                type="number"
                className={styles.batchSizeInput}
                value={batchSize}
                onChange={(e) => setBatchSize(Math.max(1, parseInt(e.target.value) || 1))}
                min={1}
                max={1000}
                title="Number of emails to process per batch"
              />
              <button
                className={styles.runBatchButton}
                onClick={handleRunBatch}
                disabled={batchRunning || pipelineStatus.never_processed_count === 0}
              >
                {batchRunning ? 'Running...' : 'Run Batch'}
              </button>
            </div>
            <button
              className={`${styles.toggleSchedulerButton} ${pipelineStatus.scheduler_enabled ? styles.schedulerActive : ''}`}
              onClick={handleToggleScheduler}
              disabled={schedulerToggling}
            >
              {schedulerToggling
                ? 'Updating...'
                : pipelineStatus.scheduler_enabled
                  ? 'Disable Scheduler'
                  : 'Enable Scheduler'}
            </button>
            <button
              className={styles.refreshButton}
              onClick={loadPipelineStatus}
              title="Refresh pipeline status"
            >
              Refresh
            </button>
            <button
              className={styles.refreshButton}
              onClick={handleResetPipeline}
              disabled={resetting}
              title="Delete all pipeline data (decisions, notifications, orders, etc.) and reset emails"
              style={{ color: '#dc3545', borderColor: '#dc3545' }}
            >
              {resetting ? 'Resetting...' : 'Reset Pipeline'}
            </button>
          </div>

          {batchMessage && (
            <div className={styles.batchMessage}>{batchMessage}</div>
          )}
        </div>
      )}

      {/* Routing Rules Panel (collapsible) */}
      {routingRules && (
        <div className={styles.routingPanel}>
          <div
            className={styles.routingHeader}
            onClick={() => setRoutingRulesOpen(!routingRulesOpen)}
          >
            <h3 className={styles.routingPanelTitle}>
              {routingRulesOpen ? '▾' : '▸'} Routing Rules
            </h3>
            <span className={styles.routingBadge}>
              {routingRules.rules.length} rules · Read-only
            </span>
          </div>

          {routingRulesOpen && (
            <div className={styles.routingBody}>
              {/* Decision Outcomes */}
              <div className={styles.routingSection}>
                <h4 className={styles.routingSectionTitle}>Decision Outcomes</h4>
                <div className={styles.outcomesGrid}>
                  {routingRules.decision_outcomes.map((o) => (
                    <div key={o.outcome} className={styles.outcomeCard}>
                      <span
                        className={styles.badge}
                        style={{ backgroundColor: DECISION_COLORS[o.outcome] || '#95a5a6' }}
                      >
                        {DECISION_LABELS[o.outcome] || o.outcome}
                      </span>
                      <span className={styles.outcomeDesc}>{o.description}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notification Type Routing */}
              <div className={styles.routingSection}>
                <h4 className={styles.routingSectionTitle}>Notification Type Routing</h4>
                <table className={styles.routingTable}>
                  <thead>
                    <tr>
                      <th>Notification Type</th>
                      <th>Category</th>
                      <th>Routing Target</th>
                    </tr>
                  </thead>
                  <tbody>
                    {routingRules.rules.map((rule) => (
                      <tr key={rule.notification_type}>
                        <td>
                          <span className={styles.monoText}>
                            {NOTIFICATION_TYPE_LABELS[rule.notification_type] || rule.notification_type}
                          </span>
                        </td>
                        <td>
                          <span className={`${styles.categoryBadge} ${styles[`category${rule.category}`] || ''}`}>
                            {rule.category}
                          </span>
                        </td>
                        <td className={styles.routingTarget}>{rule.routing_target}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Agent Guidelines */}
              <div className={styles.routingSection}>
                <h4 className={styles.routingSectionTitle}>Agent Guidelines</h4>
                <ul className={styles.guidelinesList}>
                  {routingRules.agent_guidelines.map((g, i) => (
                    <li key={i}>{g}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Error Emails Panel (collapsible) — shows ALL errors, not just DLQ */}
      {errorTotal > 0 && (
        <div className={styles.dlqPanel}>
          <div
            className={styles.dlqHeader}
            onClick={() => setErrorsOpen(!errorsOpen)}
          >
            <h3 className={styles.dlqPanelTitle} style={{ color: '#EE3C33' }}>
              {errorsOpen ? '▾' : '▸'} Error Emails
            </h3>
            <span className={styles.dlqBadge}>
              {errorTotal} email{errorTotal !== 1 ? 's' : ''} failed · Max retries: {errorMaxRetries}
            </span>
          </div>

          {errorsOpen && (
            <div className={styles.dlqBody}>
              {errorMessage && (
                <div className={styles.dlqMessage}>{errorMessage}</div>
              )}

              {errorsLoading ? (
                <div className={styles.loading}>Loading error emails...</div>
              ) : errorEmails.length === 0 ? (
                <div className={styles.empty}>No error emails.</div>
              ) : (
                <table className={styles.dlqTable}>
                  <thead>
                    <tr>
                      <th>Subject</th>
                      <th>Sender</th>
                      <th>Synced At</th>
                      <th>Retries</th>
                      <th>Status</th>
                      <th>Error</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {errorEmails.map((email) => (
                      <tr key={email.id}>
                        <td className={styles.dlqSubject}>
                          {email.subject || '(no subject)'}
                        </td>
                        <td className={styles.dlqSender}>
                          {email.sender || '-'}
                        </td>
                        <td className={styles.dlqDate}>
                          {email.synced_at ? new Date(email.synced_at).toLocaleString() : '-'}
                        </td>
                        <td className={styles.dlqRetries}>
                          {email.classification_retry_count}/{errorMaxRetries}
                        </td>
                        <td>
                          <span
                            className={styles.badge}
                            style={{
                              backgroundColor: email.is_dead_letter ? '#c0392b' : '#e67e22',
                              fontSize: '0.75rem',
                            }}
                          >
                            {email.is_dead_letter ? 'Dead Letter' : 'Retryable'}
                          </span>
                        </td>
                        <td className={styles.dlqError}>
                          <span title={email.classification_error || ''}>
                            {email.classification_error
                              ? email.classification_error.length > 100
                                ? email.classification_error.slice(0, 100) + '...'
                                : email.classification_error
                              : '-'}
                          </span>
                        </td>
                        <td>
                          <button
                            className={styles.retryButton}
                            onClick={() => handleRetryErrorEmail(email.id)}
                            disabled={errorRetrying === email.id}
                          >
                            {errorRetrying === email.id ? 'Retrying...' : 'Retry'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}

      {/* Tab Bar - Decisions vs Classifications */}
      <div className={styles.tabBar}>
        <button
          className={`${styles.tab} ${activeTab === 'decisions' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('decisions')}
        >
          Decisions
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'classifications' ? styles.tabActive : ''}`}
          onClick={() => setActiveTab('classifications')}
        >
          Classifications
        </button>
      </div>

      {/* Classifications Tab */}
      {activeTab === 'classifications' && (
        <ClassificationsPanel />
      )}

      {/* Decisions Tab */}
      {activeTab === 'decisions' && stats && (
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{stats.total_decisions}</div>
            <div className={styles.statLabel}>Total Decisions</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue} style={{ color: DECISION_COLORS.notify }}>
              {stats.notify_count}
            </div>
            <div className={styles.statLabel}>Notify</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue} style={{ color: DECISION_COLORS.unsure }}>
              {stats.unsure_count}
            </div>
            <div className={styles.statLabel}>Unsure</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue} style={{ color: DECISION_COLORS.ignore }}>
              {stats.ignore_count}
            </div>
            <div className={styles.statLabel}>Ignore</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue}>
              {stats.avg_confidence ? `${(stats.avg_confidence * 100).toFixed(0)}%` : '-'}
            </div>
            <div className={styles.statLabel}>Avg Confidence</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue}>
              {stats.avg_latency_ms ? `${Math.round(stats.avg_latency_ms)}ms` : '-'}
            </div>
            <div className={styles.statLabel}>Avg Latency</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{stats.total_tokens.toLocaleString()}</div>
            <div className={styles.statLabel}>Total Tokens</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{formatCost(stats.total_cost_usd)}</div>
            <div className={styles.statLabel}>Total Cost</div>
          </div>
        </div>
      )}

      {/* Filters Row (decisions tab only) */}
      {activeTab === 'decisions' && (
      <div className={styles.filterRow}>
        <input
          type="text"
          className={styles.searchInput}
          placeholder="Search subject, sender, title, reasoning..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <select
          value={decisionFilter}
          onChange={(e) => { setDecisionFilter(e.target.value); setPage(0); }}
          className={styles.filterSelect}
        >
          <option value="">All Decisions</option>
          <option value="notify">Notify</option>
          <option value="unsure">Unsure</option>
          <option value="ignore">Ignore</option>
        </select>
        <select
          value={notificationTypeFilter}
          onChange={(e) => { setNotificationTypeFilter(e.target.value); setPage(0); }}
          className={styles.filterSelect}
        >
          <option value="">All Types</option>
          {notificationTypes.map((t) => (
            <option key={t} value={t}>
              {NOTIFICATION_TYPE_LABELS[t] || t}
            </option>
          ))}
        </select>
        <span className={styles.totalCount}>
          {total} decision{total !== 1 ? 's' : ''}
        </span>
      </div>
      )}

      {/* Decision Table (decisions tab only) */}
      {activeTab === 'decisions' && (loading ? (
        <div className={styles.loading}>Loading decisions...</div>
      ) : decisions.length === 0 ? (
        <div className={styles.empty}>No decisions found.</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              {COLUMNS.map((col) => (
                <th
                  key={col.label}
                  className={col.sortable ? styles.sortableHeader : undefined}
                  onClick={col.sortable && col.key ? () => handleSort(col.key as SortField) : undefined}
                >
                  {col.label}
                  {col.sortable && col.key ? getSortIcon(col.key as SortField) : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {decisions.map((d) => (
              <tr
                key={d.id}
                className={styles.row}
                onClick={() => openDetail(d)}
              >
                <td className={styles.dateCell}>{formatDate(d.created_at)}</td>
                <td>
                  <span
                    className={styles.badge}
                    style={{ backgroundColor: DECISION_COLORS[d.decision] }}
                  >
                    {DECISION_LABELS[d.decision] || d.decision}
                  </span>
                </td>
                <td className={styles.typeCell}>
                  {NOTIFICATION_TYPE_LABELS[d.notification_type] || d.notification_type}
                </td>
                <td className={styles.subjectCell}>
                  <div className={styles.subjectLine}>{getEmailSubject(d.email_context)}</div>
                  <div className={styles.fromLine}>{getEmailFrom(d.email_context)}</div>
                </td>
                <td>{(d.confidence * 100).toFixed(0)}%</td>
                <td>{d.total_tokens.toLocaleString()}</td>
                <td>{formatCost(d.total_cost_usd)}</td>
                <td>{d.latency_ms}ms</td>
                <td className={styles.salespersonCell}>{d.salesperson_no || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ))}

      {/* Pagination */}
      {activeTab === 'decisions' && totalPages > 1 && (
        <div className={styles.pagination}>
          <button disabled={page === 0} onClick={() => setPage(0)}>
            First
          </button>
          <button disabled={page === 0} onClick={() => setPage(page - 1)}>
            Prev
          </button>
          <span>
            Page {page + 1} of {totalPages}
          </span>
          <button disabled={page + 1 >= totalPages} onClick={() => setPage(page + 1)}>
            Next
          </button>
          <button disabled={page + 1 >= totalPages} onClick={() => setPage(totalPages - 1)}>
            Last
          </button>
        </div>
      )}

      {/* Detail Modal */}
      {selectedDecision && (
        <DecisionDetailModal
          decision={selectedDecision}
          loading={detailLoading}
          onClose={closeDetail}
        />
      )}

      {/* Deep-link "no decision" banner — replaces silent failure of View Agent Run */}
      {deepLinkNotFound && !selectedDecision && !detailLoading && (
        <div
          className={styles.modalOverlay}
          onClick={dismissDeepLinkNotFound}
          data-testid="deep-link-not-found-overlay"
        >
          <div
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 560 }}
            data-testid="deep-link-not-found-modal"
          >
            <div className={styles.modalHeader}>
              <div className={styles.modalTitleRow}>
                <h2 className={styles.modalTitle}>No agent run found</h2>
                <button
                  className={styles.modalClose}
                  onClick={dismissDeepLinkNotFound}
                  aria-label="Close"
                >
                  &times;
                </button>
              </div>
            </div>
            <div className={styles.modalBody}>
              <p>
                The unified email pipeline has not produced a decision for this email yet.
              </p>
              <p>
                This usually means the email is still queued or got stuck mid-classification.
                Check the Pipeline Control panel above for the email&apos;s status, or use
                Reset Pipeline / Retry on the Error Emails panel.
              </p>
              <p style={{ fontSize: 12, color: '#6b7280' }}>
                Email ID: <code>{deepLinkNotFound}</code>
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// =============================================================================
// Detail Modal Component
// =============================================================================

interface DecisionDetailModalProps {
  decision: EmailMonitorDecision;
  loading: boolean;
  onClose: () => void;
}

function DecisionDetailModal({ decision: d, loading, onClose }: DecisionDetailModalProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'agent_messages' | 'email_context' | 'raw_json'>('overview');

  const formatDate = (dateStr: string) => new Date(dateStr).toLocaleString();
  const formatCost = (cost: number) => `$${cost.toFixed(4)}`;

  // Extract readable agent message info
  // Handles both old format (type/content/tool_calls) and new format (role/content/metadata)
  const renderAgentMessage = (msg: Record<string, unknown>, index: number) => {
    const msgType = String(msg.type || msg.role || 'unknown');
    const content = msg.content != null ? String(msg.content) : '';
    const name = msg.name ? String(msg.name) : undefined;

    // Support both old format (tool_calls at top level) and new format (inside metadata)
    const metadata = msg.metadata as Record<string, unknown> | undefined;
    const topLevelToolCalls = msg.tool_calls as Array<Record<string, unknown>> | undefined;
    const metadataToolCalls = metadata?.tool_calls as Array<Record<string, unknown>> | undefined;
    const metadataLlmCalls = metadata?.llm_calls as Array<Record<string, unknown>> | undefined;
    const toolCalls = topLevelToolCalls || metadataToolCalls;

    return (
      <div key={index} className={`${styles.msgBlock} ${styles[`msgBlock_${msgType}`] || ''}`}>
        <div className={styles.msgHeader}>
          <span className={styles.msgTypeLabel}>{msgType.toUpperCase()}</span>
          {name && <span className={styles.msgName}>{name}</span>}
          {metadata?.model_name != null && (
            <span className={styles.msgMeta}>
              {String(metadata.model_name)}
              {metadata.tokens_used != null ? ` · ${Number(metadata.tokens_used).toLocaleString()} tokens` : ''}
              {metadata.cost_usd != null ? ` · $${Number(metadata.cost_usd).toFixed(4)}` : ''}
            </span>
          )}
          <span className={styles.msgIndex}>#{index + 1}</span>
        </div>
        {content && (
          <pre className={styles.msgBody}>{content}</pre>
        )}
        {toolCalls && toolCalls.length > 0 && (
          <div className={styles.toolCallsSection}>
            <div className={styles.toolCallsLabel}>Tool Calls ({toolCalls.length}):</div>
            {toolCalls.map((tc, i) => {
              // Handle multiple tool call formats:
              // Old: {function: {name, arguments}} or {name, args}
              // New (ToolCallRecord): {tool_name, tool_args, result, ...}
              const toolName = String(
                tc.tool_name
                || (tc.function as Record<string, unknown>)?.name
                || tc.name
                || 'unknown',
              );
              const toolArgs =
                tc.tool_args
                || (tc.function as Record<string, unknown>)?.arguments
                || tc.args
                || tc.input
                || {};
              const toolResult = tc.result as string | undefined;

              return (
                <div key={i} className={styles.toolCall}>
                  <div className={styles.toolCallName}>{toolName}</div>
                  <pre className={styles.toolCallArgs}>
                    {typeof toolArgs === 'string'
                      ? toolArgs
                      : JSON.stringify(toolArgs, null, 2)}
                  </pre>
                  {toolResult && (
                    <div className={styles.toolResultSection}>
                      <div className={styles.toolResultLabel}>Result:</div>
                      <pre className={styles.toolCallArgs}>{toolResult}</pre>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        {metadataLlmCalls && metadataLlmCalls.length > 0 && !toolCalls?.length && (
          <div className={styles.toolCallsSection}>
            <div className={styles.toolCallsLabel}>LLM Calls ({metadataLlmCalls.length}):</div>
            {metadataLlmCalls.map((lc, i) => (
              <div key={i} className={styles.toolCall}>
                <div className={styles.toolCallName}>
                  {String(lc.model_name || lc.model || 'unknown')}
                </div>
                <pre className={styles.toolCallArgs}>
                  {JSON.stringify(lc, null, 2)}
                </pre>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className={styles.modalHeader}>
          <div className={styles.modalTitleRow}>
            <span
              className={styles.badge}
              style={{ backgroundColor: DECISION_COLORS[d.decision], fontSize: '13px', padding: '4px 12px' }}
            >
              {DECISION_LABELS[d.decision] || d.decision}
            </span>
            <h2 className={styles.modalTitle}>{d.title || 'Decision Detail'}</h2>
            <button className={styles.modalClose} onClick={onClose}>
              &times;
            </button>
          </div>
          <div className={styles.modalMeta}>
            <span>ID: {d.id}</span>
            <span>{formatDate(d.created_at)}</span>
            <span>Model: {d.model_name}</span>
            <span>Iterations: {d.agent_iterations}</span>
            <span>{d.total_tokens.toLocaleString()} tokens</span>
            <span>{formatCost(d.total_cost_usd)}</span>
            <span>{d.latency_ms}ms</span>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className={styles.tabBar}>
          {(['overview', 'agent_messages', 'email_context', 'raw_json'] as const).map((tab) => (
            <button
              key={tab}
              className={`${styles.tab} ${activeTab === tab ? styles.tabActive : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === 'overview' && 'Overview'}
              {tab === 'agent_messages' && `Agent Messages (${d.agent_messages.length})`}
              {tab === 'email_context' && 'Email Context'}
              {tab === 'raw_json' && 'Raw JSON'}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className={styles.modalBody}>
          {loading && <div className={styles.loading}>Loading full detail...</div>}

          {activeTab === 'overview' && (
            <div className={styles.overviewTab}>
              {/* Decision Summary */}
              <section className={styles.detailSection}>
                <h3>Decision</h3>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <label>Outcome</label>
                    <span>{DECISION_LABELS[d.decision] || d.decision}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <label>Confidence</label>
                    <span>{(d.confidence * 100).toFixed(1)}%</span>
                  </div>
                  <div className={styles.detailItem}>
                    <label>Notification Type</label>
                    <span>{NOTIFICATION_TYPE_LABELS[d.notification_type] || d.notification_type}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <label>Salesperson</label>
                    <span>{d.salesperson_no || 'None'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <label>Notification ID</label>
                    <span className={styles.monoText}>{d.notification_id || 'None'}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <label>Feedback</label>
                    <span>{d.feedback_processed ? 'Processed' : 'Pending'}</span>
                  </div>
                </div>
              </section>

              {/* Order Extraction (shown when agent detected an order) */}
              {d.order_info && (
                <section className={styles.detailSection}>
                  <h3>Order Extraction</h3>
                  <div className={styles.detailGrid}>
                    {d.order_info.customer_no && (
                      <div className={styles.detailItem}>
                        <label>Customer No</label>
                        <span className={styles.monoText}>{d.order_info.customer_no}</span>
                      </div>
                    )}
                    {d.order_info.customer_po_no && (
                      <div className={styles.detailItem}>
                        <label>Customer PO</label>
                        <span>{d.order_info.customer_po_no}</span>
                      </div>
                    )}
                    {d.order_info.udf_required_by && (
                      <div className={styles.detailItem}>
                        <label>Required By</label>
                        <span>{d.order_info.udf_required_by}</span>
                      </div>
                    )}
                    {d.category && (
                      <div className={styles.detailItem}>
                        <label>Category</label>
                        <span>{d.category}</span>
                      </div>
                    )}
                  </div>
                  {d.order_info.line_items && d.order_info.line_items.length > 0 && (
                    <table className={styles.orderTable}>
                      <thead>
                        <tr>
                          <th>Item Code</th>
                          <th>Qty Ordered</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.order_info.line_items.map((item, i) => (
                          <tr key={i}>
                            <td className={styles.monoText}>{item.item_code}</td>
                            <td>{item.quantity_ordered}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </section>
              )}

              {/* Reasoning */}
              <section className={styles.detailSection}>
                <h3>Agent Reasoning</h3>
                <pre className={styles.reasoningBlock}>{d.reasoning}</pre>
              </section>

              {/* Notification Message */}
              {d.message && (
                <section className={styles.detailSection}>
                  <h3>Notification Message</h3>
                  <div className={styles.notificationPreview}>
                    <div className={styles.notificationTitle}>{d.title}</div>
                    <RecommendationText text={d.message} className={styles.notificationBody} />
                  </div>
                </section>
              )}

              {/* Email Quick Info */}
              <section className={styles.detailSection}>
                <h3>Email</h3>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <label>Subject</label>
                    <span>{String(d.email_context.email_subject || d.email_context.subject || '-')}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <label>From</label>
                    <span>{String(d.email_context.email_from || d.email_context.from || '-')}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <label>Category</label>
                    <span>{String(d.email_context.classification_category || d.email_context.category || '-')}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <label>Customer</label>
                    <span>{String(d.email_context.matched_customer_name || '-')}</span>
                  </div>
                </div>
              </section>

              {/* Feedback Flow */}
              <section className={styles.detailSection}>
                <h3>Feedback</h3>
                {d.feedback ? (
                  <div className={styles.feedbackSection}>
                    <div className={styles.detailGrid}>
                      <div className={styles.detailItem}>
                        <label>User Feedback</label>
                        <span className={d.feedback.user_feedback === 'thumbs_up' ? styles.feedbackPositive : styles.feedbackNegative}>
                          {d.feedback.user_feedback === 'thumbs_up' ? 'Helpful' : 'Not Helpful'}
                        </span>
                      </div>
                      <div className={styles.detailItem}>
                        <label>Feedback Date</label>
                        <span>{d.feedback.feedback_at ? formatDate(d.feedback.feedback_at) : '-'}</span>
                      </div>
                      <div className={styles.detailItem}>
                        <label>KB Updated</label>
                        <span className={d.feedback.knowledge_updated ? styles.feedbackPositive : styles.feedbackPending}>
                          {d.feedback.knowledge_updated ? 'Yes — report_learning called' : 'No'}
                        </span>
                      </div>
                    </div>
                    {d.feedback.feedback_text && (
                      <div className={styles.feedbackTextBlock}>
                        <label>User Comment</label>
                        <p>{d.feedback.feedback_text}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className={styles.feedbackEmpty}>
                    {d.notification_id
                      ? 'No feedback submitted yet.'
                      : 'No notification created (decision was Ignore) — no feedback possible.'}
                  </div>
                )}
              </section>

              {/* Performance Metrics */}
              <section className={styles.detailSection}>
                <h3>Performance</h3>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <label>Model</label>
                    <span className={styles.monoText}>{d.model_name}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <label>Iterations</label>
                    <span>{d.agent_iterations}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <label>Total Tokens</label>
                    <span>{d.total_tokens.toLocaleString()}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <label>Cost</label>
                    <span>{formatCost(d.total_cost_usd)}</span>
                  </div>
                  <div className={styles.detailItem}>
                    <label>Latency</label>
                    <span>{d.latency_ms}ms</span>
                  </div>
                  <div className={styles.detailItem}>
                    <label>Expires</label>
                    <span>{formatDate(d.expires_at)}</span>
                  </div>
                </div>
              </section>
            </div>
          )}

          {activeTab === 'agent_messages' && (
            <div className={styles.messagesTab}>
              <p className={styles.messagesHint}>
                Full LLM message chain — system prompt, user input, tool calls, and agent responses.
              </p>
              {d.agent_messages.length === 0 ? (
                <div className={styles.empty}>No agent messages recorded.</div>
              ) : (
                d.agent_messages.map((msg, i) =>
                  renderAgentMessage(msg as Record<string, unknown>, i),
                )
              )}
            </div>
          )}

          {activeTab === 'email_context' && (
            <div className={styles.contextTab}>
              <p className={styles.messagesHint}>
                Full email context snapshot stored with this decision.
              </p>
              <pre className={styles.jsonBlock}>
                {JSON.stringify(d.email_context, null, 2)}
              </pre>
            </div>
          )}

          {activeTab === 'raw_json' && (
            <div className={styles.rawTab}>
              <p className={styles.messagesHint}>
                Complete raw decision record for debugging.
              </p>
              <pre className={styles.jsonBlock}>
                {JSON.stringify(d, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
